import type { VisualEditorPage, VisualEditorWidget } from '../../types.ts';
import type { VisualEditorDrawerCallbacks } from './VisualEditorDrawerConstants.ts';

export interface VisualEditorDragDropContext {
  drawerElement: HTMLElement | null;
  page: VisualEditorPage | null;
  callbacks: VisualEditorDrawerCallbacks | null;
  renderContent: () => void;
  showToast: (msg: string) => void;
  findWidgetRecursive: (id: string | null) => VisualEditorWidget | null;
  setJustDropped?: (val: boolean) => void;
}

export function setupDragAndDrop(ctx: VisualEditorDragDropContext): void {
  if (!ctx.drawerElement || !ctx.page) return;

  const items = ctx.drawerElement.querySelectorAll<HTMLElement>('.ve-widget-tree-item');
  let draggedId: string | null = null;

  items.forEach((item) => {
    item.addEventListener('dragstart', (e) => {
      draggedId = item.dataset.id || null;
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', draggedId || '');
      }
      requestAnimationFrame(() => {
        item.classList.add('dragging');
      });
    });

    item.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'move';
      }
      const currentId = item.dataset.id;
      if (currentId === draggedId) return;

      const rect = item.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const isAbove = e.clientY < midY;

      item.classList.toggle('drag-over-above', isAbove);
      item.classList.toggle('drag-over-below', !isAbove);
    });

    item.addEventListener('dragleave', () => {
      item.classList.remove('drag-over-above', 'drag-over-below');
    });

    item.addEventListener('dragend', () => {
      items.forEach((el) => {
        el.classList.remove('dragging', 'drag-over-above', 'drag-over-below');
      });
      ctx.setJustDropped?.(true);
      setTimeout(() => {
        ctx.setJustDropped?.(false);
      }, 150);
    });

    item.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      items.forEach((el) => {
        el.classList.remove('dragging', 'drag-over-above', 'drag-over-below');
      });

      const sourceId = e.dataTransfer?.getData('text/plain') || draggedId;
      const targetId = item.dataset.id;

      if (!sourceId || !targetId || sourceId === targetId || !ctx.page) {
        return;
      }

      const rect = item.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const isAbove = e.clientY < midY;

      const sourceIdx = ctx.page.widgets.findIndex((w) => w.contentItemId === sourceId);
      if (sourceIdx === -1) return;

      const [movedWidget] = ctx.page.widgets.splice(sourceIdx, 1);
      const targetIdx = ctx.page.widgets.findIndex((w) => w.contentItemId === targetId);
      if (targetIdx === -1) return;

      const insertIdx = isAbove ? targetIdx : targetIdx + 1;
      ctx.page.widgets.splice(insertIdx, 0, movedWidget);

      ctx.page.widgets.forEach((w, index) => {
        w.order = index;
      });

      const newOrderIds = ctx.page.widgets.map((w) => w.contentItemId);

      ctx.renderContent();
      ctx.callbacks?.onWidgetReordered(newOrderIds);
      ctx.showToast('Widgets Reordered');
    });
  });
}

export function moveWidget(
  ctx: VisualEditorDragDropContext,
  widgetId: string,
  direction: number
): void {
  if (!ctx.page) return;
  const idx = ctx.page.widgets.findIndex((w) => w.contentItemId === widgetId);
  if (idx === -1) return;

  const targetIdx = idx + direction;
  if (targetIdx < 0 || targetIdx >= ctx.page.widgets.length) return;

  const ids = ctx.page.widgets.map((w) => w.contentItemId);
  const temp = ids[idx];
  ids[idx] = ids[targetIdx];
  ids[targetIdx] = temp;

  ctx.callbacks?.onWidgetReordered(ids);
}

export function setupFlowItemsDragAndDrop(ctx: VisualEditorDragDropContext): void {
  if (!ctx.drawerElement || !ctx.page) return;

  const items = ctx.drawerElement.querySelectorAll<HTMLElement>('.ve-flow-item-card');
  let draggedId: string | null = null;
  let parentId: string | null = null;

  items.forEach((item) => {
    item.addEventListener('dragstart', (e) => {
      draggedId = item.dataset.id || null;
      parentId = item.dataset.parentId || null;
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', draggedId || '');
      }
      requestAnimationFrame(() => {
        item.classList.add('dragging');
      });
    });

    item.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'move';
      }
      const currentId = item.dataset.id;
      if (currentId === draggedId) return;

      const rect = item.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const isAbove = e.clientY < midY;

      item.classList.toggle('drag-over-above', isAbove);
      item.classList.toggle('drag-over-below', !isAbove);
    });

    item.addEventListener('dragleave', () => {
      item.classList.remove('drag-over-above', 'drag-over-below');
    });

    item.addEventListener('dragend', () => {
      items.forEach((el) => {
        el.classList.remove('dragging', 'drag-over-above', 'drag-over-below');
      });
    });

    item.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      items.forEach((el) => {
        el.classList.remove('dragging', 'drag-over-above', 'drag-over-below');
      });

      const sourceId = e.dataTransfer?.getData('text/plain') || draggedId;
      const targetId = item.dataset.id;
      const itemParentId = item.dataset.parentId || parentId;

      if (!sourceId || !targetId || sourceId === targetId || !itemParentId || !ctx.page) {
        return;
      }

      const parentWidget = ctx.findWidgetRecursive(itemParentId);
      if (!parentWidget || !parentWidget.subItems) return;

      const rect = item.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const isAbove = e.clientY < midY;

      const sourceIdx = parentWidget.subItems.findIndex((w) => w.contentItemId === sourceId);
      if (sourceIdx === -1) return;

      const [moved] = parentWidget.subItems.splice(sourceIdx, 1);
      const targetIdx = parentWidget.subItems.findIndex((w) => w.contentItemId === targetId);
      if (targetIdx === -1) return;

      const insertIdx = isAbove ? targetIdx : targetIdx + 1;
      parentWidget.subItems.splice(insertIdx, 0, moved);

      parentWidget.subItems.forEach((w, index) => {
        w.order = index;
      });

      const newOrderIds = parentWidget.subItems.map((w) => w.contentItemId);
      ctx.renderContent();
      ctx.callbacks?.onWidgetReordered(newOrderIds, itemParentId);
      ctx.showToast('Items Reordered');
    });
  });
}

export function moveSubItem(
  ctx: VisualEditorDragDropContext,
  parentId: string,
  subItemId: string,
  direction: number
): void {
  if (!ctx.page) return;
  const parent = ctx.findWidgetRecursive(parentId);
  if (!parent || !parent.subItems) return;

  const idx = parent.subItems.findIndex((s) => s.contentItemId === subItemId);
  if (idx === -1) return;

  const targetIdx = idx + direction;
  if (targetIdx < 0 || targetIdx >= parent.subItems.length) return;

  const ids = parent.subItems.map((s) => s.contentItemId);
  const temp = ids[idx];
  ids[idx] = ids[targetIdx];
  ids[targetIdx] = temp;

  ctx.callbacks?.onWidgetReordered(ids, parentId);
}
